import { useEffect } from "react";

// Marks the document with the screen that owns the page background, so
// styles.css can put that screen's full-bleed background on the document
// itself rather than on a child element. Three separate consumers read it,
// and which of them is in charge depends on the OS:
//
//   - the page's own pixels, when `apple-mobile-web-app-status-bar-style:
//     black-translucent` is in effect (iOS up to 26.0, and only then): the
//     status bar sits over the page, so the background must be on the
//     document to show behind the clock.
//   - the OS, with a flat colour, when it is not — which includes iOS 26.1,
//     where the translucent style was dropped and the web view is inset below
//     the status bar again. Measured on Ville's device (screenshot, Sept
//     2026): the strip is exactly #ffffff while both body's background-colour
//     and this meta tag were #fff4e5, so on that OS *neither* of them fills
//     it. `theme-color` is documented as ignored outright in Safari/WebKit 26
//     (see styles.css for the sources); the attribute is mirrored onto <html>
//     as well because an explicit background-colour on the root element is
//     the one remaining colour source that has never been ruled out.
//   - Android Chrome's task switcher and address bar, which do honour
//     `theme-color`.
//
// So `themeColorToken` must name the colour of the page's *topmost row of
// pixels*, not the screen's dominant colour — otherwise a band drawn from it
// and the page disagree and the join reads as a seam. Read from a
// design-system token rather than a hardcoded hex so a palette change can't
// silently desync the two.
export function useScreenBackground(screen: string, themeColorToken: string) {
  useEffect(() => {
    document.body.dataset.screen = screen;
    document.documentElement.dataset.screen = screen;

    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (meta) {
      const value = getComputedStyle(document.documentElement).getPropertyValue(themeColorToken).trim();
      if (value) meta.content = value;
    }

    return () => {
      delete document.body.dataset.screen;
      delete document.documentElement.dataset.screen;
    };
  }, [screen, themeColorToken]);
}
